/**
 * The score, as data.
 *
 * The soundtrack is written here the same way the sound effects are written in
 * `audio/cues.ts`: as a table a renderer can turn into oscillators, never as a
 * file. A track is a stack of layers; a layer is a rhythm (which sixteenths it
 * fires on), a melodic contour (which chord tone each of those hits takes) and
 * one voice description. `sequencer.ts` walks them; `conductor.ts` decides
 * which are audible. Nothing here touches the DOM or reads a clock, so the
 * whole composition is inspectable from Node.
 *
 * Composition is meant to be edited here and nowhere else.
 */

/** Every layer the score can stack. Ids are unique within a track. */
export type LayerId =
  | 'sub'
  | 'pulse'
  | 'hat'
  | 'perc'
  | 'stab'
  | 'arp'
  | 'arpSlow'
  | 'arpTwin'
  | 'lead'
  | 'pad'
  | 'tilt';

export type TrackId = 'menu' | 'solo' | 'balance' | 'twin';

export type Wave = 'sine' | 'square' | 'sawtooth' | 'triangle' | 'noise';

export interface NoteFilter {
  kind: 'lowpass' | 'highpass' | 'bandpass';
  freq: number;
  q?: number;
}

/** One sounded note. Mirrors `audio/cues.ts`'s `Voice`, plus stereo position. */
export interface NoteSpec {
  wave: Wave;
  freq: number;
  attack: number;
  decay: number;
  gain: number;
  filter?: NoteFilter;
  detune?: number;
  /** -1 hard left, 1 hard right. Omitted means centred. */
  pan?: number;
}

/** A voice description with the pitch left out; the layer supplies that. */
export type VoiceSpec = Omit<NoteSpec, 'freq'>;

export interface LayerSpec {
  id: LayerId;
  /** Level at which this layer first sounds. */
  enterLevel: number;
  /** Sixteenth indices within a bar that fire, 0..15. */
  steps: readonly number[];
  /**
   * Chord tone per fired step, cycled when shorter than `steps`. The value is
   * an index into the bar's triad and may run past it: 3 is the root an octave
   * up, 4 the third above that, and so on. `null` rests.
   */
  tones: readonly (number | null)[];
  /** Which bars of the four-bar phrase this layer plays. Omitted means all. */
  bars?: readonly number[];
  /** Octave of the chord root. `sub` sits at 1, which puts A at 55 Hz. */
  octave: number;
  voice: VoiceSpec;
}

export interface TrackSpec {
  id: TrackId;
  baseBpm: number;
  layers: readonly LayerSpec[];
}

export const STEPS_PER_BAR = 16;
export const BARS = 4;

/** Ceiling on the tempo curve. Past this the sixteenths stop reading as pulses. */
const MAX_BPM = 168;
const BPM_PER_LEVEL = 2.5;

/**
 * Tempo against level. Deliberately flatter than the gravity curve — gravity
 * roughly doubles by level 10, the tempo climbs about a fifth over the same
 * range, so the music reads as pressure building rather than as an alarm.
 */
export function bpmForLevel(base: number, level: number): number {
  return Math.min(MAX_BPM, Math.max(base, base + BPM_PER_LEVEL * (level - 1)));
}

/**
 * Semitones above A for each tone of the bar's chord: Am, F, C, G. Written out
 * rather than derived from a scale because the three major chords would come
 * out minor if the triad were stacked from A minor degrees alone.
 */
const CHORDS: readonly (readonly number[])[] = [
  [0, 3, 7], // Am — A  C  E
  [-4, 0, 3], // F  — F  A  C
  [3, 7, 10], // C  — C  E  G
  [-2, 2, 5], // G  — G  B  D
];

/** Semitones from A for the root of the given bar of the progression. */
export function progressionRoot(bar: number): number {
  return CHORDS[bar % CHORDS.length][0];
}

/** The chord tone `index` of the given bar, wrapping up an octave past the triad. */
export function chordTone(bar: number, index: number): number {
  const chord = CHORDS[bar % CHORDS.length];
  const octaves = Math.floor(index / chord.length);
  return chord[((index % chord.length) + chord.length) % chord.length] + octaves * 12;
}

/** Equal temperament against A4 = 440 Hz. `degree` is semitones from A. */
export function noteHz(degree: number, octave: number): number {
  return 440 * 2 ** (degree / 12 + (octave - 4));
}

// --------------------------------------------------------------------- layers

const SUB: LayerSpec = {
  id: 'sub',
  enterLevel: 1,
  steps: [0, 8],
  tones: [0],
  octave: 1,
  voice: {
    wave: 'sine',
    attack: 0.005,
    decay: 0.3,
    gain: 0.55,
    filter: { kind: 'lowpass', freq: 220 },
  },
};

/** The engine of the track: a sixteenth pulse, filtered down to a thud. */
const PULSE: LayerSpec = {
  id: 'pulse',
  enterLevel: 1,
  steps: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
  tones: [0],
  octave: 2,
  voice: {
    wave: 'sawtooth',
    attack: 0.002,
    decay: 0.07,
    gain: 0.16,
    filter: { kind: 'lowpass', freq: 700, q: 4 },
  },
};

const HAT: LayerSpec = {
  id: 'hat',
  enterLevel: 3,
  steps: [2, 6, 10, 14],
  tones: [0],
  octave: 4,
  voice: {
    wave: 'noise',
    attack: 0.001,
    decay: 0.035,
    gain: 0.1,
    filter: { kind: 'highpass', freq: 7000 },
  },
};

/** BALANCE's replacement for the hat: on the grid, but never on the beat. */
const PERC: LayerSpec = {
  id: 'perc',
  enterLevel: 3,
  steps: [3, 6, 11, 13],
  tones: [0],
  octave: 4,
  voice: {
    wave: 'noise',
    attack: 0.001,
    decay: 0.05,
    gain: 0.12,
    filter: { kind: 'bandpass', freq: 2600, q: 2 },
  },
};

const STAB: LayerSpec = {
  id: 'stab',
  enterLevel: 5,
  steps: [6, 14],
  tones: [1, 2],
  octave: 3,
  voice: {
    wave: 'sawtooth',
    attack: 0.004,
    decay: 0.22,
    gain: 0.12,
    detune: 7,
    filter: { kind: 'lowpass', freq: 1400, q: 1 },
  },
};

const ARP: LayerSpec = {
  id: 'arp',
  enterLevel: 8,
  steps: [0, 2, 4, 6, 8, 10, 12, 14],
  tones: [0, 1, 2, 3, 4, 3, 2, 1],
  octave: 3,
  voice: {
    wave: 'square',
    attack: 0.002,
    decay: 0.09,
    gain: 0.08,
    filter: { kind: 'lowpass', freq: 2600, q: 1 },
  },
};

/** TWIN's second voice: the same figure an eighth late, sitting on the right. */
const ARP_TWIN: LayerSpec = {
  ...ARP,
  id: 'arpTwin',
  steps: [2, 4, 6, 8, 10, 12, 14, 0],
  octave: 4,
  voice: { ...ARP.voice, gain: 0.055, pan: 0.6 },
};

/** Half-speed arpeggio, for the menu. */
const ARP_SLOW: LayerSpec = {
  id: 'arpSlow',
  enterLevel: 1,
  steps: [0, 4, 8, 12],
  tones: [0, 2, 4, 1],
  octave: 3,
  voice: {
    wave: 'square',
    attack: 0.004,
    decay: 0.2,
    gain: 0.06,
    filter: { kind: 'lowpass', freq: 1800, q: 1 },
  },
};

/** An answering phrase, held back to two bars in four so it never nags. */
const LEAD: LayerSpec = {
  id: 'lead',
  enterLevel: 12,
  steps: [4, 10, 12],
  tones: [4, 3, 2],
  bars: [1, 3],
  octave: 4,
  voice: {
    wave: 'sine',
    attack: 0.01,
    decay: 0.35,
    gain: 0.07,
    filter: { kind: 'lowpass', freq: 3000 },
  },
};

const PAD: LayerSpec = {
  id: 'pad',
  enterLevel: 1,
  steps: [0],
  tones: [1],
  octave: 2,
  voice: {
    wave: 'sawtooth',
    attack: 0.8,
    decay: 1.6,
    gain: 0.1,
    detune: 6,
    filter: { kind: 'lowpass', freq: 500, q: 1 },
  },
};

/**
 * BALANCE only. The conductor scales this one by the platform's risk, so the
 * lean is audible as a drone opening up before it is visible as a tilt.
 */
const TILT: LayerSpec = {
  id: 'tilt',
  enterLevel: 1,
  steps: [0, 8],
  tones: [0],
  octave: 2,
  voice: {
    wave: 'sawtooth',
    attack: 0.3,
    decay: 0.9,
    gain: 0.09,
    detune: 30,
    filter: { kind: 'bandpass', freq: 300, q: 6 },
  },
};

// --------------------------------------------------------------------- tracks

export const TRACKS: Record<TrackId, TrackSpec> = {
  menu: { id: 'menu', baseBpm: 96, layers: [PAD, SUB, ARP_SLOW] },
  solo: { id: 'solo', baseBpm: 118, layers: [SUB, PULSE, HAT, STAB, ARP, LEAD] },
  balance: { id: 'balance', baseBpm: 118, layers: [SUB, PULSE, PERC, STAB, ARP, LEAD, TILT] },
  twin: { id: 'twin', baseBpm: 118, layers: [SUB, PULSE, HAT, STAB, ARP, ARP_TWIN, LEAD] },
};
